import { CSSProperties, FC, useId, useState } from "react";
import PhoneInput, { Country } from "react-phone-number-input";
import "react-phone-number-input/style.css";
import Text, { TextOverrideProps } from "@src/lib/ui/Text";
import { cn } from "@src/lib/utils/cn";
import { inputFieldClassName } from "@src/lib/ui/Input";
import LockOutlineIcon from "@src/lib/icons/18px/lock-outline";

type Props = {
  value: string | undefined;
  label?: string;
  labelTextProps?: TextOverrideProps;
  onChange: (value: string | undefined) => void;
  className?: string;
  disabled?: boolean;
  style?: CSSProperties;
  error?: boolean;
  height?: "40px" | "32px";
  subtext?: string;
  required?: boolean;
  dataTestId?: string;
};

const PhoneNumberInput: FC<Props> = ({
  value,
  label = "Mobile Phone Number",
  labelTextProps,
  onChange,
  className,
  disabled,
  style,
  error,
  height = "32px",
  subtext,
  required,
  dataTestId,
}) => {
  const [countryCode, setCountryCode] = useState<Country | undefined>("US");
  const inputId = useId();
  const subtextId = `${inputId}-subtext`;
  return (
    <div className={cn("flex flex-col self-stretch", className)} style={style}>
      <label htmlFor={inputId} className="mb-1">
        {/* Legacy typography: callers override via TextOverrideProps (type/fontWeight). */}
        <Text
          type={"P3"}
          fontWeight={"SemiBold"}
          className="text-text-primary"
          {...labelTextProps}
        >
          {label}
          {required && "*"}
        </Text>
      </label>
      <div className="relative ml-1">
        <PhoneInput
          data-testid={dataTestId}
          placeholder={countryCode === "US" && "(201) 555-5555"}
          defaultCountry="US"
          value={value}
          numberInputProps={{
            className: cn(
              inputFieldClassName,
              "box-border w-full text-ellipsis",
              height === "32px" ? "px-2" : "px-2.5",
            ),
            id: inputId,
            "aria-invalid": error || undefined,
            "aria-describedby": subtext ? subtextId : undefined,
            style: {
              height,
            },
          }}
          onCountryChange={(country) => setCountryCode(country)}
          onChange={onChange}
          disabled={disabled}
          limitMaxLength
        />
        {disabled && (
          <LockOutlineIcon className="absolute top-1/2 right-3 -translate-y-1/2 text-icon-tertiary" />
        )}
      </div>
      {subtext && (
        <Text
          id={subtextId}
          variant="fine-print-title"
          className={error ? "text-status-error-text" : "text-text-tertiary"}
          style={{ margin: "4px 0 0 0" }}
        >
          {subtext}
        </Text>
      )}
    </div>
  );
};

export default PhoneNumberInput;
